import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import translate from '@translate';
import Table from 'src/components/Table';
import { OrderIcon } from 'src/components/SkladonIcon/Icons';
import {
    GridFilterType,
    GridSortType,
    ProductDetailGridTableRefType,
    RowActionPropsType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType,
} from 'src/types/table';
import { ProductOrdersType } from 'src/types/productDetail';
import { UserSettingsType } from 'src/types/userSettings';
import { ConstantsCodeType, ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { colors } from 'src/themes/default';
import { formatNumber } from 'src/utils/formatNumber';
import colorOptionFce from 'src/utils/colorOptionFce';
import filterUnique from 'src/utils/filterUnique';
import { orderTypeCodes } from 'src/resources/constantsCodes/constants';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import { getStatusIcon, renderTrackingNumber } from '../OrderDetailContainer/functions';
import StatusLabel from 'src/components/StatusLabel';
import { REQUIRED_ORDERS_GRID_COLUMNS } from '../AllOrdersContainer/constants';
import {
    defaultVisibleProductOrdersColumns,
    productOrdersAllColumns,
} from './productOrdersTableColumns';

const StyledContainer = styled('div')({
    padding: '16px 0',
});

const OPTIONS_SESSION_NAME = 'productOrdersTable';

const DEFAULT_ITEMS = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

/** Sloupce nutné pro logiku tabulky (šedá pcs, product set), nejsou v hlavičce. */
const HIDDEN_GRID_COLUMNS = ['LINE_STATE', 'IS_PRODUCT_SET', 'PRODUCT_ID'] as const;

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: DEFAULT_ITEMS,
    columns: [...defaultVisibleProductOrdersColumns],
};

function getInitialTableOptions(): TablePropsOptionsType {
    try {
        const session = JSON.parse(sessionStorage.getItem(OPTIONS_SESSION_NAME) || 'null');
        if (session && typeof session.items === 'number' && ROWS_PER_PAGE_OPTIONS.includes(session.items)) {
            return { ...initTableOptions, ...session, columns: session.columns || initTableOptions.columns };
        }
    } catch {
        // ignore invalid session
    }
    return initTableOptions;
}

export function buildProductOrdersGridPayload(
    options: TablePropsOptionsType,
    _idProduct: string
): {
    page: number;
    items: number;
    sorts: GridSortType[];
    filters: GridFilterType[][];
    columns: string[];
} {
    const items = ROWS_PER_PAGE_OPTIONS.includes(options.items) ? options.items : DEFAULT_ITEMS;
    const userConditions = (options.filters || []).flat().filter((c) => c && c.column);
    return {
        page: options.page,
        items,
        sorts: [options.sort],
        filters: userConditions.length ? [[...userConditions]] : [[]],
        columns: [
            ...REQUIRED_ORDERS_GRID_COLUMNS,
            ...options.columns.filter((column) => !column.allwaysShow).map((column) => column.key),
            ...HIDDEN_GRID_COLUMNS,
        ].filter(filterUnique),
    };
}

export const isProductSetRow = (row: TableRowType): boolean =>
    row.IS_PRODUCT_SET === true || row.IS_PRODUCT_SET === 1;

export const isDeactivatedLine = (row: TableRowType): boolean => String(row.LINE_STATE ?? '').toUpperCase() === 'D';

export interface ProductOrdersTablePropsType {
    data: ProductOrdersType | null;
    loading?: boolean;
    onFetchData: (payload: ReturnType<typeof buildProductOrdersGridPayload>) => void;
    idProduct: string;
    orderStatusCodes: ConstantsCodeType[];
    orderStatusCodesObject: ConstantsCodesObjectType;
    shipperCodes: ConstantsCodeType[];
    serviceCodes: ConstantsCodeType[];
    paymentCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    currencyCodes: ConstantsCodeType[];
    orderMarketplacesCodes: ConstantsCodeType[];
    permissionOrderDetail: boolean;
    userSettingsData: UserSettingsType | null;
    editUserSettings: (newUserSettings: UserSettingsType) => void;
}

const ProductOrdersTable = forwardRef<ProductDetailGridTableRefType, ProductOrdersTablePropsType>((props, ref) => {
    const tableRef = useRef<TableRefType>(null);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(getInitialTableOptions);

    const rows: TableRowType[] = props.data?.data ?? [];
    const totalCount = props.data?.totalCount ?? 0;

    const loadTableData = useCallback(() => {
        if (props.idProduct && tableOptions.columns.length) {
            props.onFetchData(buildProductOrdersGridPayload(tableOptions, props.idProduct));
        }
    }, [props.idProduct, props.onFetchData, tableOptions]);

    useImperativeHandle(
        ref,
        (): ProductDetailGridTableRefType => ({
            exportTable: (type, filename, getPrintRows, extendedColmns) =>
                tableRef.current?.exportTable(type, filename, getPrintRows, extendedColmns),
            exportTableGrid: (type, filename, getGridParams, requestUrl, extendedColmns, extendOptions) =>
                tableRef.current?.exportTableGrid(
                    type,
                    filename,
                    getGridParams,
                    requestUrl,
                    extendedColmns,
                    extendOptions
                ),
            getGridParams: () => buildProductOrdersGridPayload(tableOptions, props.idProduct),
        }),
        [tableOptions, props.idProduct]
    );

    useEffect(() => {
        loadTableData();
    }, [tableOptions]);

    const handleChangeOptions = useCallback((newOptions: TablePropsOptionsType) => {
        setTableOptions(newOptions);
    }, []);

    const customRender = useCallback(
        (column: TableColumnType, row: TableRowType) => {
            const value = row[column.key];
            switch (column.key) {
                case 'TIMESTAMP':
                case 'CREATED_AT':
                case 'SLA_DATE':
                case 'RELEASE_FROM_SUSPENDED_AT':
                case 'PREORDER_RELEASE_AT':
                case 'WAITING_FOR_SHIPMENT_AT':
                case 'SHIPMENT_ORDERED_AT':
                case 'IN_PROCESS_AT':
                case 'SCANNED_AT':
                case 'PACKED_AT':
                case 'WAITING_FOR_CARRIER_AT':
                case 'SHIPPED_AT':
                case 'DELIVERED_AT':
                case 'RETURNING_BACK_AT':
                case 'RETURNED_BACK_AT':
                case 'CANCELED_AT':
                case 'LAST_STATUS_CHECK_AT':
                case 'UPDATED_AT':
                    return value ? moment(value as string).format('DD.MM.YYYY HH:mm') : '';
                case 'INVOICE_DATE':
                case 'REQUIRED_EXPEDITION_DATE':
                    return value ? moment(value as string).format('DD.MM.YYYY') : '';
                case 'STATUS':
                    return (
                        <StatusLabel
                            icon={getStatusIcon(row.STATUS)}
                            option={props.orderStatusCodesObject[row.STATUS]}
                        />
                    );
                case 'TRACKING_NUMBER':
                    return renderTrackingNumber(row, 'TRACKING_NUMBER');
                case 'RETURN_TRACKING_NUMBER':
                    return renderTrackingNumber(row, 'RETURN_TRACKING_NUMBER');
                case 'EXCHANGE_TRACKING_NUMBER':
                    return renderTrackingNumber(row, 'EXCHANGE_TRACKING_NUMBER');
                case 'PRINT_RETURN_LABEL': {
                    if (row.PRINT_RETURN_LABEL !== undefined) {
                        const printValue = row.PRINT_RETURN_LABEL;
                        const active = printValue === 1 || printValue === true;
                        return (
                            <span style={{ color: active ? '#4caf50' : 'red' }}>
                                {active ? translate('utils.boolCodes.yes') : translate('utils.boolCodes.no')}
                            </span>
                        );
                    }
                    return value;
                }
                case 'COUNT':
                    if (isDeactivatedLine(row)) {
                        return <span style={{ color: colors.text_T3 }}>{formatNumber(value)}</span>;
                    }
                    return formatNumber(value);
                case 'MISSING_COUNT':
                    if (isProductSetRow(row)) {
                        return <span />;
                    }
                    if (!isDeactivatedLine(row) && row.MISSING_COUNT && Number(row.MISSING_COUNT) > 0) {
                        return <span style={{ color: colors.alert_O }}>{formatNumber(value)}</span>;
                    }
                    if (isDeactivatedLine(row)) {
                        return <span style={{ color: colors.text_T3 }}>{formatNumber(value)}</span>;
                    }
                    return formatNumber(value);
                case 'CONFIRMED':
                    if (isProductSetRow(row)) {
                        return <span />;
                    }
                    if (isDeactivatedLine(row)) {
                        return <span style={{ color: colors.text_T3 }}>{formatNumber(value)}</span>;
                    }
                    return formatNumber(value);
                default:
                    return value;
            }
        },
        [props.orderStatusCodesObject]
    );

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <OrderIcon />,
            label: translate('orders.action.orderDetail'),
            getLink: (row) => `/orders/${row.ID}/`,
            hiddenFce: () => !props.permissionOrderDetail,
        },
    ];

    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        orderStatusCodes: props.orderStatusCodes,
        shipperCodes: props.shipperCodes,
        countryOptionsCodes,
        serviceCodes: props.serviceCodes,
        paymentCodes: props.paymentCodes,
        boolNumberCodes: props.boolNumberCodes,
        currencyCodes: props.currencyCodes,
        orderTypeCodes,
        orderMarketplacesCodes: props.orderMarketplacesCodes,
    };

    return (
        <StyledContainer data-testid="product-orders-table">
            <Table
                ref={tableRef}
                rowIdKey="ID"
                rows={rows}
                allColumns={productOrdersAllColumns}
                columsLabelPrefix="orders.table"
                options={tableOptions}
                totalCount={totalCount}
                loading={props.loading}
                customRender={customRender}
                heightVariant="top-tab"
                fullHeight
                optionsSessionName={OPTIONS_SESSION_NAME}
                selectOptions={tableSelectOptions}
                onChangeOptions={handleChangeOptions}
                showClearFilters
                rowActions={tableRowActions}
                optionStyleFce={colorOptionFce}
                userSettingsName="showProductOrdersTableColumns"
                userSettingsData={props.userSettingsData}
                editUserSettings={props.editUserSettings}
            />
        </StyledContainer>
    );
});

ProductOrdersTable.displayName = 'ProductOrdersTable';

export default ProductOrdersTable;
